import {
  Button,
  SettingsGroup,
  SettingsGroupItem,
  SettingsGroupSwitchItem,
} from '@sb/design-system';
import type { RestoreTrainingRun } from '@sb/feathers-types';

import { RestoreAlert } from './RestoreAlert';

interface RestoreModelSelectionPanelProps {
  trainingRuns: RestoreTrainingRun[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  /** False when this robot's operation mode means updater-bot never
   *  downloads trained models — the rows are still queued, they just wait. */
  modelDownloadsEnabled: boolean;
  operationMode?: string;
  disabled?: boolean;
}

/**
 * Which of the backup's AI models to bring over.
 *
 * Models are not inside the backup — the weights live in the cloud training
 * run — so a restore re-requests them onto this robot. That request is
 * separate from the destructive apply, which is why it is a choice at all.
 */
export function RestoreModelSelectionPanel({
  trainingRuns,
  selectedIds,
  onChange,
  modelDownloadsEnabled,
  operationMode,
  disabled,
}: RestoreModelSelectionPanelProps) {
  if (trainingRuns.length === 0) {
    return (
      <section
        className="tw-flex tw-flex-col"
        data-testid="restore-confirmation-models-region"
      >
        <h5 className="tw-heading-40 tw-pl-16">AI models</h5>

        <SettingsGroup>
          <SettingsGroupItem className="tw-text-label-tertiary">
            This backup does not name any trained models.
          </SettingsGroupItem>
        </SettingsGroup>
      </section>
    );
  }

  const allSelected = selectedIds.length === trainingRuns.length;

  const toggle = (trainingRunId: string) => {
    onChange(
      selectedIds.includes(trainingRunId)
        ? selectedIds.filter((id) => id !== trainingRunId)
        : [...selectedIds, trainingRunId],
    );
  };

  return (
    <section
      className="tw-flex tw-flex-col tw-gap-8"
      data-testid="restore-confirmation-models-region"
    >
      <h5 className="tw-heading-40 tw-pl-16 tw-justify-between tw-pr-4">
        AI models to restore
        <Button
          size={32}
          variant="Flat"
          className="tw-rounded-6"
          data-testid="restore-confirmation-models-select-all-button"
          disabled={disabled}
          onClick={() =>
            onChange(
              allSelected ? [] : trainingRuns.map((run) => run.trainingRunId),
            )
          }
        >
          {allSelected ? 'Clear all' : 'Select all'}
        </Button>
      </h5>

      <SettingsGroup>
        {trainingRuns.map((run, index) => (
          <SettingsGroupSwitchItem
            key={run.trainingRunId}
            data-testid={`restore-confirmation-model-switch-${index}`}
            label={
              <span className="tw-truncate" title={run.trainingRunId}>
                {run.trainingRunId}
              </span>
            }
            secondaryLabel={
              run.epoch === null ? undefined : `epoch ${run.epoch}`
            }
            checked={selectedIds.includes(run.trainingRunId)}
            disabled={disabled}
            onChange={() => toggle(run.trainingRunId)}
          />
        ))}
      </SettingsGroup>

      {!modelDownloadsEnabled && (
        <RestoreAlert data-testid="restore-confirmation-models-disabled-banner">
          This robot is in <b>{operationMode ?? 'this'}</b> mode, which does not
          download AI models. Anything you select is queued in the cloud and
          stays queued until the robot is switched to GPU mode or local
          inference is enabled — it will not download now.
        </RestoreAlert>
      )}
    </section>
  );
}
